import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Brain, MessageSquare, ShieldCheck, HelpCircle, Building2, Mic, Mail, CalendarDays } from 'lucide-react';
import Layout from '../components/layout/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const VOICE_OPTIONS = [
  // Voci Madrelingua Italiana
  { id: 'mT0eqrjKfAPl6gQBlfBa', name: 'Chiara', desc: 'Italiana - Professionale e versatile', gender: 'F', native: true },
  { id: 'jFW3ckrPY2w2mRxLpLQj', name: 'Carla', desc: 'Italiana - Formale, accento fiorentino', gender: 'F', native: true },
  { id: 'zFA34HbdHBvF8WhlSusK', name: 'Nora', desc: 'Italiana - Vivace e chiara', gender: 'F', native: true },
  { id: 'ZRKmc75tGxpIMNTEiwe0', name: 'Mimmi', desc: 'Italiana - Giovane e solare', gender: 'F', native: true },
  { id: 'f8NAZK1ciwrVujah7clz', name: 'Valerio', desc: 'Italiano - Caldo e rassicurante', gender: 'M', native: true },
  { id: 'sp6F311QRVXR53QPGIgK', name: 'Luca', desc: 'Italiano - Calmo e affidabile', gender: 'M', native: true },
  // Voci Multilingua (accento internazionale)
  { id: 'XrExE9yKIg1WjnnlVkGX', name: 'Matilda', desc: 'Multilingua - Rassicurante', gender: 'F', native: false },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sara', desc: 'Multilingua - Professionale', gender: 'F', native: false },
  { id: 'onwK4e9ZLuTAKqWW03F9', name: 'Daniele', desc: 'Multilingua - Autorevole', gender: 'M', native: false },
];

const GOOGLE_VOICE_OPTIONS = [
  { id: 'it-IT-Neural2-A', name: 'Giulia', desc: 'Neural2 - Femminile, naturale', gender: 'F' },
  { id: 'it-IT-Neural2-C', name: 'Marco', desc: 'Neural2 - Maschile, naturale', gender: 'M' },
  { id: 'it-IT-Wavenet-A', name: 'Elena', desc: 'WaveNet - Femminile, chiara', gender: 'F' },
  { id: 'it-IT-Wavenet-B', name: 'Giovanni', desc: 'WaveNet - Maschile, profondo', gender: 'M' },
  { id: 'it-IT-Wavenet-C', name: 'Roberto', desc: 'WaveNet - Maschile, versatile', gender: 'M' },
  { id: 'it-IT-Wavenet-D', name: 'Francesca', desc: 'WaveNet - Femminile, calda', gender: 'F' },
];

export default function AIPromptSettings() {
  const { user } = useAuth();
  const canEditApiKey = user?.email?.toLowerCase() === 'fabrizio.guerra@telcavoip.eu';
  const [settings, setSettings] = useState({
    nome_centro: 'Centro Medico',
    benvenuto_paziente_conosciuto: 'Buongiorno {nome}, benvenuto al {centro}. Come posso aiutarla oggi?',
    benvenuto_paziente_nuovo: 'Buongiorno, benvenuto al {centro}. Sono l\'assistente vocale per le prenotazioni. Come posso aiutarla?',
    tono_voce: 'Professionale e cordiale, come una segretaria esperta. Usa il Lei.',
    regole_personalizzate: '',
    faq: [],
    servizi_prioritari: '',
    messaggio_chiusura: 'Grazie per aver chiamato il {centro}. Arrivederci!',
    regole_prenotazione: 'Proponi sempre 2-3 date disponibili. Chiedi sempre conferma prima di fissare.',
    info_centro: '',
    voice_id: 'EXAVITQu4vr4xnSDxMaL',
    tts_provider: 'elevenlabs',
    google_voice: 'it-IT-Neural2-A',
    voice_mode: 'legacy',
    realtime_voice: 'alloy',
    realtime_model: 'gpt-realtime-mini',
    ai_language: 'it',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const [realtimeAvailable, setRealtimeAvailable] = useState(false);
  const [rtTestResult, setRtTestResult] = useState(null);
  const [rtTesting, setRtTesting] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const [res, gRes] = await Promise.all([
        api.get('/settings/ai-prompt'),
        api.get('/settings/google-voices'),
      ]);
      if (res.data && !res.data.detail) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
      if (gRes.data) {
        setGoogleAvailable(gRes.data.available);
        setRealtimeAvailable(!!gRes.data.realtime_available);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/ai-prompt', settings);
      setSaved(true);
      toast.success('Istruzioni AI salvate!');
      setTimeout(() => setSaved(false), 3000);
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));
  const addFaq = () => setSettings(prev => ({ ...prev, faq: [...prev.faq, { domanda: '', risposta: '' }] }));
  const updateFaq = (idx, field, value) => {
    setSettings(prev => {
      const faq = [...prev.faq];
      faq[idx] = { ...faq[idx], [field]: value };
      return { ...prev, faq };
    });
  };
  const removeFaq = (idx) => setSettings(prev => ({ ...prev, faq: prev.faq.filter((_, i) => i !== idx) }));

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="ai-prompt-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Brain className="text-violet-500" size={28} /> Istruzioni AI
            </h1>
            <p className="text-sm text-slate-500 mt-1">Personalizza come l'assistente vocale AI risponde ai pazienti</p>
          </div>
          <button onClick={handleSave} disabled={saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all shadow-lg ${saved ? 'bg-emerald-500 text-white shadow-emerald-200' : 'bg-sky-500 text-white hover:bg-sky-600 shadow-sky-200'}`} data-testid="ai-save-btn">
            {saved ? <><ShieldCheck size={18} /> Salvato!</> : <><Save size={18} /> {saving ? 'Salvataggio...' : 'Salva'}</>}
          </button>
        </div>

        {/* Modalita AI */}
        <div className="bg-gradient-to-r from-violet-50 to-sky-50 rounded-xl border border-violet-200 p-6 shadow-sm" data-testid="voice-mode-selector">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={20} className="text-violet-500" />
            <h2 className="text-lg font-semibold text-slate-800">Modalit&agrave; AI Vocale</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Scegli il motore vocale per le chiamate</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={() => updateField('voice_mode', 'realtime')}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                settings.voice_mode === 'realtime' ? 'border-violet-500 bg-white ring-2 ring-violet-200' : 'border-slate-200 bg-white hover:border-violet-300'
              }`}
              data-testid="mode-realtime">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-violet-700">GPT-4o Realtime Voice</span>
                <span className="text-[9px] bg-violet-200 text-violet-800 px-1.5 py-0.5 rounded-full font-bold">RACCOMANDATO</span>
              </div>
              <p className="text-xs text-slate-600">
                Comprensione vocale diretta — nessun errore di trascrizione. Latenza minima (~1s). Ideale per uso medico professionale.
              </p>
            </button>
            <button onClick={() => updateField('voice_mode', 'legacy')}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                settings.voice_mode !== 'realtime' ? 'border-sky-500 bg-white ring-2 ring-sky-200' : 'border-slate-200 bg-white hover:border-sky-300'
              }`}
              data-testid="mode-legacy">
              <span className="text-sm font-bold text-sky-700">Whisper + GPT + TTS</span>
              <p className="text-xs text-slate-600 mt-1">Pipeline tradizionale: trascrizione Whisper, GPT-4o testo, voce Google/ElevenLabs. Maggiore controllo sulla voce.</p>
            </button>
          </div>

          {/* Realtime voice selector */}
          {settings.voice_mode === 'realtime' && (
            <div className="mt-4 pt-4 border-t border-violet-200" data-testid="realtime-voice-section">
              {/* API Key section - only visible to superadmin */}
              {canEditApiKey && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-violet-600 mb-1.5">Chiave API OpenAI (per Realtime Voice)</label>
                  <input
                    type="password"
                    value={settings.openai_api_key || ''}
                    onChange={e => updateField('openai_api_key', e.target.value)}
                    placeholder="sk-proj-..."
                    className="w-full px-3 py-2 border border-violet-200 rounded-lg text-sm font-mono"
                    data-testid="openai-api-key-input"
                  />
                <p className="text-[10px] text-slate-400 mt-1">La chiave viene salvata in modo sicuro nel database della clinica</p>
                <button
                  onClick={async () => {
                    setRtTesting(true);
                    setRtTestResult(null);
                    try {
                      const res = await api.get('/settings/realtime-test');
                      setRtTestResult(res.data);
                    } catch (e) {
                      setRtTestResult({ success: false, error: e.response?.data?.detail || e.message });
                    }
                    setRtTesting(false);
                  }}
                  disabled={rtTesting}
                  className="mt-2 px-4 py-2 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-all"
                  data-testid="test-realtime-btn"
                >
                  {rtTesting ? 'Testando connessione...' : 'Testa Connessione Realtime'}
                </button>
                {rtTestResult && (
                  <div className={`mt-2 p-3 rounded-lg text-xs ${rtTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="realtime-test-result">
                    <p className={`font-bold ${rtTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {rtTestResult.success ? 'CONNESSIONE OK' : 'CONNESSIONE FALLITA'}
                    </p>
                    <p className="text-slate-600 mt-1">Step: {rtTestResult.step}</p>
                    <p className="text-slate-600">Modello: {rtTestResult.realtime_model || 'n/a'}</p>
                    <p className="text-slate-600">Key: {rtTestResult.api_key_source || 'n/a'} ({rtTestResult.api_key_prefix || ''})</p>
                    {rtTestResult.configured_model && <p className="text-green-600">Modello configurato: {rtTestResult.configured_model}</p>}
                    {rtTestResult.error_message && <p className="text-red-600 font-mono mt-1">Errore: {rtTestResult.error_message}</p>}
                    {rtTestResult.error && <p className="text-red-600 font-mono mt-1">{rtTestResult.error}</p>}
                  </div>
                )}
              </div>
              )}
              <p className="text-xs font-medium text-violet-600 mb-2">Modello Realtime</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  {id: 'gpt-realtime-mini', label: 'Mini', desc: 'Economico, veloce'},
                  {id: 'gpt-realtime-2', label: 'Standard', desc: 'Ragionamento avanzato'},
                ].map(m => (
                  <button key={m.id} onClick={() => updateField('realtime_model', m.id)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      settings.realtime_model === m.id
                        ? 'bg-violet-500 text-white ring-2 ring-violet-300'
                        : 'bg-white border border-slate-200 text-slate-700 hover:border-violet-300'
                    }`} data-testid={`rtmodel-${m.id}`}>
                    {m.label} <span className="text-[10px] opacity-70">({m.desc})</span>
                  </button>
                ))}
              </div>
              <p className="text-xs font-medium text-violet-600 mb-2">Voce Realtime</p>
              <div className="flex flex-wrap gap-2">
                {[
                  {id: 'marin', label: 'Marin', desc: 'Alta qualità'},
                  {id: 'cedar', label: 'Cedar', desc: 'Alta qualità'},
                  {id: 'alloy', label: 'Alloy', desc: 'Neutra'},
                  {id: 'ash', label: 'Ash', desc: 'Maschile'},
                  {id: 'ballad', label: 'Ballad', desc: 'Espressiva'},
                  {id: 'coral', label: 'Coral', desc: 'Femminile'},
                  {id: 'echo', label: 'Echo', desc: 'Maschile chiara'},
                  {id: 'sage', label: 'Sage', desc: 'Professionale'},
                  {id: 'shimmer', label: 'Shimmer', desc: 'Brillante'},
                  {id: 'verse', label: 'Verse', desc: 'Profonda'},
                ].map(v => (
                  <button key={v.id} onClick={() => updateField('realtime_voice', v.id)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      settings.realtime_voice === v.id
                        ? 'bg-violet-500 text-white ring-2 ring-violet-300'
                        : 'bg-white border border-slate-200 text-slate-700 hover:border-violet-300'
                    }`} data-testid={`rtvoice-${v.id}`}>
                    {v.label} <span className="text-[10px] opacity-70">({v.desc})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Voce AI (Legacy mode only) */}
        {settings.voice_mode !== 'realtime' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="voice-selector">
          <div className="flex items-center gap-2 mb-4">
            <Mic size={20} className="text-sky-500" />
            <h2 className="text-lg font-semibold text-slate-800">Voce dell'Assistente</h2>
          </div>

          {/* TTS Provider Selector */}
          <div className="mb-5 p-4 bg-slate-50 rounded-lg" data-testid="tts-provider-selector">
            <p className="text-xs font-medium text-slate-600 mb-3">Provider vocale</p>
            <div className="flex gap-3">
              <button onClick={() => updateField('tts_provider', 'elevenlabs')}
                className={`flex-1 p-3 rounded-xl border-2 transition-all text-left ${
                  settings.tts_provider !== 'google' ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200' : 'border-slate-200 hover:border-violet-300'
                }`} data-testid="provider-elevenlabs">
                <p className="font-semibold text-sm text-slate-800">ElevenLabs</p>
                <p className="text-[10px] text-slate-500 mt-0.5">Qualita premium, voci ultra-realistiche</p>
              </button>
              <button onClick={() => updateField('tts_provider', 'google')}
                disabled={!googleAvailable}
                className={`flex-1 p-3 rounded-xl border-2 transition-all text-left ${
                  settings.tts_provider === 'google' ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200' : 'border-slate-200 hover:border-sky-300'
                } ${!googleAvailable ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="provider-google">
                <p className="font-semibold text-sm text-slate-800">Google Cloud TTS</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {googleAvailable ? 'Piu veloce, economico, buona qualita' : 'Non configurato (richiede API Key Google)'}
                </p>
              </button>
            </div>
          </div>

          {/* Google Voices */}
          {settings.tts_provider === 'google' && (
            <div className="mb-5" data-testid="google-voices-section">
              <p className="text-xs font-semibold text-sky-600 mb-2 uppercase tracking-wider">Voci Google Cloud (Italiano)</p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {GOOGLE_VOICE_OPTIONS.map(v => (
                  <button key={v.id} onClick={() => updateField('google_voice', v.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      settings.google_voice === v.id ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200' : 'border-slate-200 hover:border-sky-300 hover:bg-slate-50'
                    }`} data-testid={`gvoice-${v.id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        v.gender === 'F' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'
                      }`}>{v.gender}</span>
                      <span className="font-medium text-slate-800">{v.name}</span>
                      <span className="text-[9px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full font-semibold">Google</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{v.desc}</p>
                    {settings.google_voice === v.id && <span className="text-xs text-sky-600 font-medium mt-1 block">Selezionata</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ElevenLabs Voices (shown when provider is elevenlabs) */}
          {settings.tts_provider !== 'google' && (
            <>
          <p className="text-xs text-slate-400 mb-4">Scegli la voce che i pazienti sentiranno al telefono</p>
          
          <p className="text-xs font-semibold text-emerald-600 mb-2 uppercase tracking-wider">Madrelingua Italiana</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {VOICE_OPTIONS.filter(v => v.native).map(v => (
              <button key={v.id} onClick={() => updateField('voice_id', v.id)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${settings.voice_id === v.id ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'}`}
                data-testid={`voice-${v.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${v.gender === 'F' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'}`}>
                    {v.gender}
                  </span>
                  <span className="font-medium text-slate-800">{v.name}</span>
                  <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">IT</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">{v.desc}</p>
                {settings.voice_id === v.id && <span className="text-xs text-emerald-600 font-medium mt-1 block">Selezionata</span>}
              </button>
            ))}
          </div>

          <p className="text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Multilingua</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {VOICE_OPTIONS.filter(v => !v.native).map(v => (
              <button key={v.id} onClick={() => updateField('voice_id', v.id)}
                className={`p-3 rounded-xl border-2 transition-all text-left ${settings.voice_id === v.id ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200' : 'border-slate-200 hover:border-sky-300 hover:bg-slate-50'}`}
                data-testid={`voice-${v.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${v.gender === 'F' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'}`}>
                    {v.gender}
                  </span>
                  <span className="font-medium text-slate-700 text-sm">{v.name}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1.5">{v.desc}</p>
                {settings.voice_id === v.id && <span className="text-xs text-sky-600 font-medium mt-1 block">Selezionata</span>}
              </button>
            ))}
          </div>
            </>
          )}

          {/* Voice Clone section */}
          <div className="mt-5 pt-5 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Mic size={16} className="text-violet-500" />
              <p className="text-sm font-semibold text-slate-700">Voce Personalizzata (Voice Clone)</p>
            </div>
            <p className="text-xs text-slate-400 mb-3">Carica un file audio di 1-2 minuti di parlato italiano per creare una voce personalizzata</p>
            <div className="flex items-center gap-3">
              <label className="flex-1 flex items-center gap-3 px-4 py-3 bg-violet-50 border-2 border-dashed border-violet-200 rounded-xl cursor-pointer hover:bg-violet-100 transition-colors">
                <Mic size={20} className="text-violet-400" />
                <div>
                  <p className="text-sm font-medium text-violet-700">Carica file audio</p>
                  <p className="text-[10px] text-violet-400">MP3, WAV, M4A (max 10MB)</p>
                </div>
                <input type="file" accept="audio/*" className="hidden" data-testid="voice-clone-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('audio', file);
                    formData.append('name', settings.nome_centro || 'Voce Clinica');
                    try {
                      const token = localStorage.getItem('access_token');
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/clone`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: formData
                      });
                      const data = await res.json();
                      if (data.voice_id) {
                        updateField('voice_id', data.voice_id);
                        updateField('custom_voice_name', data.name);
                        toast.success(`Voce "${data.name}" creata con successo!`);
                      } else {
                        toast.error(data.detail || 'Errore nella creazione della voce');
                      }
                    } catch {
                      toast.error('Errore nel caricamento');
                    }
                  }}
                />
              </label>
              {settings.custom_voice_name && (
                <div className="px-3 py-2 bg-violet-100 rounded-lg">
                  <p className="text-xs text-violet-700 font-medium">Voce attiva:</p>
                  <p className="text-sm text-violet-800 font-bold">{settings.custom_voice_name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Nome Centro */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={20} className="text-sky-500" />
            <h2 className="text-lg font-semibold text-slate-800">Identita del Centro</h2>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Nome del centro (come lo dira l'AI)</label>
            <input type="text" value={settings.nome_centro} onChange={e => updateField('nome_centro', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400" placeholder="Centro Medico CFR Moncalieri" />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-1">Lingua dell'AI</label>
            <select value={settings.ai_language || 'it'} onChange={e => updateField('ai_language', e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 bg-white"
              data-testid="ai-language-select">
              <option value="auto">Multilingue (rilevamento automatico)</option>
              <option value="it">Italiano</option>
              <option value="en">English</option>
              <option value="pl">Polski (Polacco)</option>
              <option value="fr">Francais (Francese)</option>
              <option value="es">Espanol (Spagnolo)</option>
              <option value="de">Deutsch (Tedesco)</option>
            </select>
            <p className="text-xs text-slate-400 mt-1">
              {settings.ai_language === 'auto'
                ? "L'AI rileva la lingua del paziente e risponde nella stessa lingua"
                : "L'AI rispondera sempre nella lingua selezionata"}
            </p>
          </div>
        </div>

        {/* Messaggi di Benvenuto */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare size={20} className="text-emerald-500" />
            <h2 className="text-lg font-semibold text-slate-800">Messaggi di Benvenuto</h2>
          </div>
          <p className="text-xs text-slate-400 mb-4">Usa {'{nome}'} per il nome del paziente e {'{centro}'} per il nome del centro</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Paziente conosciuto (numero in rubrica)</label>
              <textarea value={settings.benvenuto_paziente_conosciuto} onChange={e => updateField('benvenuto_paziente_conosciuto', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-400 resize-none" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Paziente nuovo (numero sconosciuto)</label>
              <textarea value={settings.benvenuto_paziente_nuovo} onChange={e => updateField('benvenuto_paziente_nuovo', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-400 resize-none" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Messaggio di chiusura</label>
              <textarea value={settings.messaggio_chiusura} onChange={e => updateField('messaggio_chiusura', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-400 resize-none" rows={2} />
            </div>
          </div>
        </div>

        {/* Tono e Comportamento */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Brain size={20} className="text-violet-500" />
            <h2 className="text-lg font-semibold text-slate-800">Tono e Comportamento</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Tono di voce</label>
              <textarea value={settings.tono_voce} onChange={e => updateField('tono_voce', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-400 resize-none" rows={2}
                placeholder="Es. Professionale ma amichevole, usa il Lei, sii conciso..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Istruzioni personalizzate per l'AI</label>
              <textarea value={settings.regole_personalizzate} onChange={e => updateField('regole_personalizzate', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-400 resize-none" rows={5}
                placeholder={"Es:\n- Non proporre mai appuntamenti il sabato\n- Se il paziente chiede un'urgenza, indirizzalo al pronto soccorso\n- Non parlare mai di prezzi"} />
            </div>
          </div>
        </div>

        {/* Regole Prenotazione */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={20} className="text-amber-500" />
            <h2 className="text-lg font-semibold text-slate-800">Regole di Prenotazione</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Regole per le prenotazioni</label>
              <textarea value={settings.regole_prenotazione} onChange={e => updateField('regole_prenotazione', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-400 resize-none" rows={4}
                placeholder={"Es:\n- Proponi sempre 2-3 date e chiedi conferma\n- Per le prime visite, lascia 45 minuti di slot"} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Servizi prioritari</label>
              <textarea value={settings.servizi_prioritari} onChange={e => updateField('servizi_prioritari', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-400 resize-none" rows={2}
                placeholder="Es. Visita cardiologica, Ecografia, Visita ortopedica..." />
            </div>
          </div>
        </div>

        {/* Info Centro */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={20} className="text-teal-500" />
            <h2 className="text-lg font-semibold text-slate-800">Informazioni sul Centro</h2>
          </div>
          <textarea value={settings.info_centro} onChange={e => updateField('info_centro', e.target.value)}
            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-400 resize-none" rows={4}
            placeholder={"Es:\n- Indirizzo: Via Roma 1, Moncalieri (TO)\n- Orari: lun-ven 8:00-20:00, sab 8:00-13:00\n- Portare tessera sanitaria"} />
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <HelpCircle size={20} className="text-blue-500" />
              <h2 className="text-lg font-semibold text-slate-800">FAQ - Domande Frequenti</h2>
            </div>
            <button onClick={addFaq} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"><Plus size={16} /> Aggiungi FAQ</button>
          </div>
          <p className="text-xs text-slate-400 mb-4">L'AI usera queste risposte quando i pazienti fanno domande comuni</p>
          {settings.faq.length === 0 ? (
            <div className="text-center py-6 bg-slate-50 rounded-lg"><p className="text-sm text-slate-400">Nessuna FAQ configurata.</p></div>
          ) : (
            <div className="space-y-3">
              {settings.faq.map((faq, idx) => (
                <div key={idx} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <input type="text" value={faq.domanda} onChange={e => updateFaq(idx, 'domanda', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400" placeholder="Domanda del paziente..." />
                      <textarea value={faq.risposta} onChange={e => updateFaq(idx, 'risposta', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 resize-none" rows={2} placeholder="Risposta dell'AI..." />
                    </div>
                    <button onClick={() => removeFaq(idx)} className="text-red-400 hover:text-red-600 mt-2"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Calendar Default View */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="calendar-view-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl flex items-center justify-center">
              <CalendarDays size={18} className="text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800 text-base">Vista Calendario Predefinita</h2>
              <p className="text-xs text-slate-500">Scegli quale vista appuntamenti aprire per default</p>
            </div>
          </div>
          <div className="flex gap-2">
            {[
              { value: 'day', label: 'Giorno' },
              { value: 'week', label: 'Settimana' },
              { value: 'month', label: 'Mese' }
            ].map(v => (
              <button
                key={v.value}
                onClick={() => setSettings({ ...settings, calendar_default_view: v.value })}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  (settings.calendar_default_view || 'day') === v.value
                    ? 'bg-sky-500 text-white shadow-md shadow-sky-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`default-view-${v.value}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Report Email */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="report-email-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-800 text-base">Report Chiamate via Email</h2>
                <p className="text-xs text-slate-500">Ricevi un resoconto completo di ogni chiamata AI</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={settings.report_email_enabled || false}
                onChange={e => setSettings({...settings, report_email_enabled: e.target.checked})} data-testid="report-email-toggle" />
              <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
            </label>
          </div>
          {settings.report_email_enabled && (
            <div className="space-y-3 mt-3">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Email del Centro Medico</label>
                <input type="email" value={settings.report_email_address || ''}
                  onChange={e => setSettings({...settings, report_email_address: e.target.value})}
                  placeholder="segreteria@centromedico.it"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                  data-testid="report-email-input" />
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
                <p className="font-medium text-slate-600 mb-1">Cosa riceverai per ogni chiamata:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Numero chiamante e nome paziente</li>
                  <li>Trascrizione completa della conversazione AI</li>
                  <li>Esito: prenotazione, annullamento, richiesta info, ecc.</li>
                  <li>Oggetto email con data/ora e tipo per ricerca rapida</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Transfer to Operator */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="transfer-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-xl">
                <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              </div>
              <div>
                <h2 className="font-semibold text-slate-800 text-base">Trasferimento a Operatore</h2>
                <p className="text-xs text-slate-500">Numero del centralino o receptionist per il fallback umano</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={!!settings.transfer_number}
                onChange={e => { if (!e.target.checked) setSettings({...settings, transfer_number: ''}); }}
                data-testid="transfer-toggle" />
              <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Numero Centralino / Receptionist</label>
              <input type="tel" value={settings.transfer_number || ''}
                onChange={e => setSettings({...settings, transfer_number: e.target.value})}
                placeholder="+39 0427 881500"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                data-testid="transfer-number-input" />
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-xs text-orange-700">
              <p className="font-medium text-orange-800 mb-1">Quando l'AI trasferisce la chiamata:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Il paziente chiede esplicitamente di parlare con un operatore</li>
                <li>Emergenza medica segnalata dal paziente</li>
                <li>L'AI non riesce a capire la richiesta dopo 2 tentativi</li>
                <li>Richieste amministrative complesse (assicurazioni, reclami)</li>
              </ul>
              <p className="mt-2 text-orange-600">Se il numero non e' configurato, l'AI continuera' a gestire la chiamata autonomamente.</p>
            </div>
          </div>
        </div>

        {/* Save Button bottom */}
        <div className="flex justify-end pb-4">
          <button onClick={handleSave} disabled={saving}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all shadow-lg ${saved ? 'bg-emerald-500 text-white shadow-emerald-200' : 'bg-sky-500 text-white hover:bg-sky-600 shadow-sky-200'}`}
            data-testid="ai-save-btn">
            {saved ? <><ShieldCheck size={18} /> Salvato!</> : <><Save size={18} /> {saving ? 'Salvataggio...' : 'Salva Istruzioni'}</>}
          </button>
        </div>
      </div>
    </Layout>
  );
}
